import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, Send } from 'lucide-react';
import { useQuoteModal } from '../../contexts/QuoteModalContext';
import { api } from '../../lib/api';
import ChoiceChips from './quote/ChoiceChips';
import ArtworkField from './quote/ArtworkField';
import EventFields, { Select } from './quote/EventFields';
import {
  BUDGETS, CLIENT_TYPES, DECORATIONS, EVENT_CLIENT, INITIAL_QUOTE, INPUT, LABEL, PRODUCTS, QUANTITIES,
  toPayload, validateQuote,
} from './quote/quoteOptions';

function TextField({ id, label, error, inputRef, ...props }) {
  return (
    <div>
      <label className={LABEL} htmlFor={id}>{label}</label>
      <input id={id} ref={inputRef} className={`${INPUT} ${error ? 'border-red-500/60' : ''}`} data-testid={id} {...props} />
      {error && <p className="text-xs text-red-400 mt-1">{error}</p>}
    </div>
  );
}

function useModalChrome(open, closeModal, firstFieldRef) {
  useEffect(() => {
    if (!open) return undefined;
    document.body.style.overflow = 'hidden';
    setTimeout(() => firstFieldRef.current?.focus(), 60);
    const onKey = (e) => e.key === 'Escape' && closeModal();
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', onKey);
    };
  }, [open, closeModal, firstFieldRef]);
}

export default function QuoteModal() {
  const { open, closeModal } = useQuoteModal();
  const navigate = useNavigate();
  const [form, setForm] = useState(INITIAL_QUOTE);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const firstFieldRef = useRef(null);
  useModalChrome(open, closeModal, firstFieldRef);

  if (!open) return null;

  const set = (field, value) => {
    setForm((f) => ({ ...f, [field]: value }));
    if (errors[field]) setErrors((er) => ({ ...er, [field]: undefined }));
  };
  const update = (field) => (e) => set(field, e.target.value);
  const setArtwork = (fn) => setForm((f) => ({ ...f, artwork: fn(f.artwork) }));
  const isEvent = form.client_type === EVENT_CLIENT;

  const onSubmit = async (ev) => {
    ev.preventDefault();
    const e = validateQuote(form);
    setErrors(e);
    if (Object.keys(e).length) {
      setSubmitError('Please fill in the highlighted fields.');
      return;
    }
    setSubmitting(true);
    setSubmitError('');
    try {
      await api.submitQuote(toPayload(form));
      closeModal();
      navigate('/thank-you', { state: { name: form.name, email: form.email, isEvent } });
      setForm(INITIAL_QUOTE);
    } catch (err) {
      setSubmitError(err.message || 'Could not submit your quote. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-start md:items-center justify-center p-4 md:p-8 overflow-y-auto" aria-modal="true" role="dialog" data-testid="quote-modal">
      <button type="button" aria-label="Close quote form" onClick={closeModal} className="fixed inset-0 bg-black/85 backdrop-blur-sm cursor-default" />
      <div className="relative w-full max-w-4xl bg-black border border-white/10 rounded-2xl shadow-2xl shadow-sky-500/10 my-8">
        <button type="button" aria-label="Close" onClick={closeModal} className="absolute top-4 right-4 z-10 w-10 h-10 rounded-full border border-white/15 text-[#f4efe6] flex items-center justify-center hover:bg-white/5 hover:border-[#00b8ff] transition-colors" data-testid="quote-close-btn">
          <X className="w-5 h-5" />
        </button>
        <form onSubmit={onSubmit} noValidate className="p-6 md:p-10 flex flex-col gap-7">
          <div>
            <h2 className="font-display text-[#f4efe6] uppercase text-4xl md:text-6xl tracking-tight">Get a Production Quote</h2>
            <p className="text-[#cfc8bb] mt-3">Tell us what you want made. Fields marked * are required.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <TextField id="quote-name" label="Name *" inputRef={firstFieldRef} value={form.name} onChange={update('name')} placeholder="Your name" error={errors.name} />
            <TextField id="quote-email" label="Email *" type="email" value={form.email} onChange={update('email')} placeholder="you@company.com" error={errors.email} />
            <TextField id="quote-phone" label="Phone" type="tel" value={form.phone} onChange={update('phone')} placeholder="(310) 000-0000" />
            <TextField id="quote-company" label="Company or artist name" value={form.company} onChange={update('company')} placeholder="Brand, band or company" />
          </div>
          <ChoiceChips label="I am a" name="client-type" required options={CLIENT_TYPES} value={form.client_type} onChange={(v) => set('client_type', v)} error={errors.client_type} />
          {isEvent && <EventFields form={form} update={update} errors={errors} />}
          <ChoiceChips label="Products" name="products" required multiple options={PRODUCTS} value={form.products} onChange={(v) => set('products', v)} error={errors.products} />
          <ChoiceChips label="Decoration" name="decoration" required multiple options={DECORATIONS} value={form.decoration} onChange={(v) => set('decoration', v)} error={errors.decoration} />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            <Select id="q-quantity" label="Quantity *" value={form.quantity} onChange={update('quantity')} options={QUANTITIES} placeholder="Select range" error={errors.quantity} testid="quote-quantity" />
            <TextField id="quote-deadline" label="Deadline" type="date" value={form.deadline} onChange={update('deadline')} className={`${INPUT} [color-scheme:dark]`} />
            <Select id="q-budget" label="Budget (optional)" value={form.budget} onChange={update('budget')} options={BUDGETS} placeholder="Select range" testid="quote-budget" />
          </div>
          <ArtworkField files={form.artwork} setFiles={setArtwork} error={errors.artwork} />
          <div>
            <label className={LABEL} htmlFor="quote-details">Project details</label>
            <textarea id="quote-details" value={form.details} onChange={update('details')} rows={5} placeholder="Colors, placements, sizes, garment preferences, anything else we should know…" className={`${INPUT} resize-y`} data-testid="quote-details" />
          </div>
          <div className="flex flex-col sm:flex-row sm:items-center gap-4">
            <button type="submit" disabled={submitting} data-testid="quote-submit-btn" className="inline-flex items-center justify-center gap-2 bg-[#00b8ff] text-black rounded-full px-8 py-3.5 font-semibold text-[15px] hover:bg-[#f4efe6] transition-colors duration-200 group disabled:opacity-60 disabled:cursor-not-allowed">
              {submitting ? 'Sending…' : 'Request a Quote'}
              <Send className="w-4 h-4 transition-transform duration-200 group-hover:translate-x-1" />
            </button>
            {submitError && <p data-testid="quote-error" className="text-sm text-red-400">{submitError}</p>}
          </div>
        </form>
      </div>
    </div>
  );
}
